/*----------------------------------
- COMPONENT: GLOSSARY TEXT
----------------------------------*/

/**
 * @docs docs/features/glossary
 *
 * Renders a plain catalog string, wrapping the first use of each glossary term in a hover bubble.
 *
 * The catalogs stay plain strings: nothing about the bubbles leaks into the copy, so a rewrite of a
 * sentence never has to think about markup. Only the shared renderers opt in.
 *
 * @rule First use per section, never per page. A section owns its `seen` set through
 *       `GlossarySection`; without a provider every string is its own section, which is the right
 *       default for a one-paragraph surface such as an FAQ answer.
 * @rule Never used inside a heading, a link, a button or a term's own definition. A `<dfn>` with a
 *       card inside a link would nest interactive content, and defining a word inside its own
 *       definition is circular.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

import { splitGlossaryRun } from '@uniquedomains/components/glossary/split';

import { installGlossaryEdgeFlip } from './edgeFlip';
import GlossaryTerm from './GlossaryTerm';

/*----------------------------------
- SECTION SCOPE
----------------------------------*/

const GlossarySectionContext = React.createContext<Set<string> | null>(null);

type GlossarySectionProps = {
    children: React.ReactNode;
};

/**
 * Scopes "first use" to one section of a page.
 *
 * The set lives as long as the provider and is filled as the subtree renders, in document order,
 * which is the order a reader meets the words in. Each `GlossaryText` remembers which terms it
 * claimed, so rendering it again gives the same bubbles instead of finding them all taken.
 */
export const GlossarySection = ({ children }: GlossarySectionProps) => {
    const seen = React.useMemo(() => new Set<string>(), []);
    return <GlossarySectionContext.Provider value={seen}>{children}</GlossarySectionContext.Provider>;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

type GlossaryTextProps = {
    children: string;
};

const GlossaryText = ({ children }: GlossaryTextProps) => {
    // Installs one delegated listener for the whole document, on whichever run of text hydrates
    // first. Nothing here affects what was served: the definition is already in the HTML.
    React.useEffect(() => installGlossaryEdgeFlip(), []);

    const sectionSeen = React.useContext(GlossarySectionContext);
    const claimed = React.useRef<readonly string[]>([]);

    // No provider: the string is its own section, which is what a standalone FAQ answer wants.
    const { segments, claimed: nextClaimed } = splitGlossaryRun(
        children,
        sectionSeen ?? new Set<string>(),
        claimed.current,
    );
    claimed.current = nextClaimed;

    return (
        <>
            {segments.map((segment, index) =>
                typeof segment === 'string' ? (
                    <React.Fragment key={`text-${index}`}>{segment}</React.Fragment>
                ) : (
                    <GlossaryTerm key={`term-${segment.term.id}`} term={segment.term}>
                        {segment.text}
                    </GlossaryTerm>
                ),
            )}
        </>
    );
};

export default GlossaryText;
